import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Box, Typography, Paper, Chip, Stack, Grid, Divider } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { formatarParaReais, converterStatusPagamentoParaTexto, converterTipoStatusLancamentoParaTexto } from "../../services/utilitarios";
import { StatusPagamento, TipoStatusLancamento } from "../../services/tipos";

interface ProcessamentoLotePreviewDto {
    nomeLocatario: string;
    quantidade: number;
    valorTotal: number;
}

interface ModalConfirmacaoLoteProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    dados: ProcessamentoLotePreviewDto[];
    titulo: string;
    tipoAcao: 'baixar' | 'cancelar' | 'excluir';
    filtros: {
        dataInicio: Date | null;
        dataFim: Date | null;
        status: any;
        idLocacao: string;
    };
    tela: 'pagamentos' | 'lançamentos'
}

export function ModalConfirmacaoLote({ open, onClose, onConfirm, dados, titulo, tipoAcao, filtros, tela }: ModalConfirmacaoLoteProps) {

    const columns: GridColDef[] = [
        { field: 'nomeLocatario', headerName: 'Locatário', flex: 1 },
        { field: 'quantidade', headerName: 'Qtd. Pagamentos', width: 150, align: 'center', headerAlign: 'center' },
        {
            field: 'valorTotal',
            headerName: 'Valor Total',
            width: 150,
            renderCell: (params) => formatarParaReais(params.value)
        },
    ];

    const rows = dados.map((item, index) => ({
        id: index,
        ...item
    }));

    const totalValor = dados.reduce((acc, curr) => acc + curr.valorTotal, 0);
    const totalQtd = dados.reduce((acc, curr) => acc + curr.quantidade, 0);

    const getColor = () => {
        if (tipoAcao === 'baixar') return 'success';
        if (tipoAcao === 'cancelar') return 'error';
        return 'error'; // excluir
    };

    const getMainColor = () => {
        if (tipoAcao === 'baixar') return '#2e7d32';
        if (tipoAcao === 'cancelar') return '#d32f2f';
        return '#d32f2f'; // excluir
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            maxWidth="md"
            fullWidth
        >
            <DialogTitle id="alert-dialog-title">
                {titulo}
            </DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-description" component="div">

                    <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'text.secondary' }}>
                            FILTROS APLICADOS NESTA OPERAÇÃO
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
                            {filtros.idLocacao && (
                                <Chip label="Locação Selecionada" color="primary" size="small" variant="outlined" />
                            )}

                            {(filtros.dataInicio && filtros.dataFim) ? (
                                <Chip
                                    label={`Período: ${filtros.dataInicio.toLocaleDateString('pt-BR')} até ${filtros.dataFim.toLocaleDateString('pt-BR')}`}
                                    variant="outlined"
                                    size="small"
                                />
                            ) : (
                                <Chip label="Período: Todo o histórico" variant="outlined" size="small" />
                            )}

                            <Chip
                                label={`Status: ${tela == 'pagamentos' ? converterStatusPagamentoParaTexto(filtros.status) : converterTipoStatusLancamentoParaTexto(filtros.status)}`}
                                variant="outlined"
                                size="small"
                                color={
                                    filtros.status === (tela === 'pagamentos'
                                        ? StatusPagamento.Todos
                                        : TipoStatusLancamento.Todos)
                                        ? 'default'
                                        : 'warning'
                                }
                            />
                        </Stack>
                    </Paper>

                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Confira abaixo os locatários que serão afetados por esta ação de <strong>{tipoAcao === 'baixar' ? 'Baixa' : tipoAcao === 'cancelar' ? 'Cancelamento' : 'Exclusão'}</strong> em lote.
                    </Typography>
                </DialogContentText>

                <div style={{ height: 350, width: '100%' }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        disableRowSelectionOnClick
                        hideFooter
                        density="compact"
                        sx={{
                            '& .MuiDataGrid-cell:focus': { outline: 'none' },
                        }}
                    />
                </div>

                <Paper elevation={3} sx={{ mt: 3, p: 2, borderTop: `4px solid ${getMainColor()}` }}>
                    <Grid container alignItems="center" justifyContent="space-between">
                        <Grid item>
                            <Typography variant="overline" display="block" color="text.secondary" lineHeight={1}>
                                TOTAL DE PAGAMENTOS
                            </Typography>
                            <Typography variant="h4" color="text.primary" fontWeight="bold">
                                {totalQtd}
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Divider orientation="vertical" flexItem sx={{ height: 40, mx: 2, display: { xs: 'none', sm: 'block' } }} />
                        </Grid>
                        <Grid item>
                            <Typography variant="overline" display="block" color="text.secondary" align="right" lineHeight={1}>
                                VALOR TOTAL AFETADO
                            </Typography>
                            <Typography variant="h4" color={tipoAcao === 'baixar' ? 'success.main' : 'error.main'} fontWeight="bold" align="right">
                                {formatarParaReais(totalValor)}
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>

            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
                <Button onClick={onClose} color="inherit" variant="text">Cancelar</Button>
                <Button
                    onClick={onConfirm}
                    variant="contained"
                    color={getColor()}
                    autoFocus
                    size="large"
                    sx={{ px: 4 }}
                >
                    Confirmar {tipoAcao === 'baixar' ? 'Baixa' : tipoAcao === 'cancelar' ? 'Cancelamento' : 'Exclusão'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
